import React, { useEffect, useState } from 'react';
import { Input } from '../common/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../common/Popover';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/utils/utils';
import { ReactDayPickerProps } from './ReactDayPicker';
import { Button } from '../common/Button';

type CustimeTipmePickerProps = ReactDayPickerProps & {
    value: any,
    onChange: any,
    className?: string,
    popoverContentClassName?: string,
    disableChangeMinute?: boolean
};

const CustomTimePicker = ({ value, onChange, className, popoverContentClassName, disableChangeMinute = false, ...props }: CustimeTipmePickerProps) => {
    const [hour, setHour] = useState(value ? parseInt(value.split(':')[0]) : 12);
    const [minute, setMinute] = useState(value ? parseInt(value.split(':')[1]) : 0);
    const [isOpen, setIsOpen] = useState(false);

    const formatTime = num => String(num).padStart(2, '0');

    useEffect(() => {
        if (value) {
            const [h, m] = value.split(':').map(Number);
            setHour(h);
            setMinute(m);
        }
    }, [value]);

    const handleHourChange = (amount) => {
        setHour((prev) => {
            const newHour = (prev + amount + 24) % 24;
            onChange(`${formatTime(newHour)}:${formatTime(minute)}`);
            return newHour;
        });
    };

    const handleMinuteChange = (amount) => {
        setMinute((prev) => {
            const newMinute = (prev + amount + 60) % 60;
            onChange(`${formatTime(hour)}:${formatTime(newMinute)}`);
            return newMinute;
        });
    };

    const handleHourInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newHour = value === '' ? 0 : parseInt(value);
        if (!isNaN(newHour)) {
            const cappedHour = Math.min(Math.max(newHour, 0), 23);
            setHour(cappedHour);
            onChange(`${formatTime(cappedHour)}:${formatTime(minute)}`);
        }
    };

    const handleMinuteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newMinute = value === '' ? 0 : parseInt(value);
        if (!isNaN(newMinute)) {
            const cappedMinute = Math.min(Math.max(newMinute, 0), 59);
            setMinute(cappedMinute);
            onChange(`${formatTime(hour)}:${formatTime(cappedMinute)}`);
        }
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger className='w-full' asChild>
                <div className='relative w-full'>
                    <Input
                        readOnly
                        value={`${formatTime(hour)}:${formatTime(minute)}`}
                        onClick={() => setIsOpen(true)}
                        className={cn(
                            'text-sm text-left text-text h-12 w-full pr-10 cursor-pointer',
                            className
                        )}
                    />
                    <ChevronDown
                        className={cn(
                            'absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer transition-opacity',
                            isOpen && 'hidden'
                        )}
                        size={14} // Tăng kích thước để dễ nhìn hơn
                        onClick={() => setIsOpen(true)}
                    />
                </div>
            </PopoverTrigger>

            <PopoverContent
                align='start'
                className={cn(
                    'w-full p-2 bg-white shadow-md rounded-lg flex justify-around items-center gap-4 z-[1100]',
                    popoverContentClassName
                )}
            >
                <div className='flex flex-col items-center'>
                    <Button onClick={() => handleHourChange(1)} className='text-red-500 hover:text-primary' size='icon' variant='ghost'>
                        <ChevronUp />
                    </Button>
                    <Input
                        readOnly
                        type='number'
                        value={formatTime(hour)}
                        onChange={handleHourInputChange}
                        className={cn(
                            'text-xl text-center w-12 h-10 p-0 border-none focus:ring-0 bg-light cursor-default',
                            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' // Tắt mũi tên
                        )}
                        min={0}
                        max={23}
                    />
                    <Button onClick={() => handleHourChange(-1)} className='text-red-500 hover:text-primary' size='icon' variant='ghost'>
                        <ChevronDown />
                    </Button>
                </div>

                <span className='text-xl'>:</span>

                <div className='flex flex-col items-center'>
                    {!disableChangeMinute && (
                        <Button onClick={() => handleMinuteChange(30)} className='text-red-500 hover:text-primary' size='icon' variant='ghost'>
                            <ChevronUp />
                        </Button>
                    )}
                    <Input
                        readOnly
                        type='number'
                        value={formatTime(minute)}
                        onChange={disableChangeMinute ? undefined : handleMinuteInputChange}
                        className={cn(
                            'text-xl text-center w-12 h-10 p-0 border-none focus:ring-0 bg-light cursor-default',
                            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' // Tắt mũi tên
                        )}
                        min={0}
                        max={59}
                    />
                    {!disableChangeMinute && (
                        <Button onClick={() => handleMinuteChange(-30)} className='text-red-500 hover:text-primary' size='icon' variant='ghost'>
                            <ChevronDown />
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default CustomTimePicker;
